<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="scores-panel-container" @click.stop>
      <!-- 標題列 -->
      <div class="panel-header">
        <button class="back-btn" @click="handleBack">
          <svg-icon icon-class="arrow-pre" />
        </button>
        <h3 class="panel-title">{{ t('gameActManager.scoresTitle') }} - {{ activityName }}</h3>
        <!-- 發送成績按鈕（僅在 webview + HT 名單時顯示） -->
        <button v-if="showHTFeatures && !loading && scores.length > 0" class="send-scores-btn" @click="handleSendScores">
          <svg-icon icon-class="send" />
          <span>{{ t('gameActManager.sendScores') }}</span>
        </button>
      </div>

      <!-- 內容區 -->
      <div class="panel-body">
        <!-- 載入中 -->
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>{{ t('gameActManager.loadingScores') }}</p>
        </div>

        <!-- 無成績資料 -->
        <div v-else-if="!loading && scores.length === 0" class="no-data-container">
          <svg-icon icon-class="empty-data" class="empty-icon" />
          <p>{{ t('gameActManager.noScoresYet') }}</p>
        </div>

        <!-- 成績列表 -->
        <div v-else class="scores-list">
          <!-- 統計資訊 -->
          <div class="stats-summary">
            <div class="stat-item">
              <span class="stat-label">{{ t('gameActManager.totalSubmissions') }}:</span>
              <span class="stat-value">{{ totalCount }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">{{ t('gameActManager.averageScore') }}:</span>
              <span class="stat-value">{{ averageScore.toFixed(1) }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">{{ t('gameActManager.highestScore') }}:</span>
              <span class="stat-value">{{ highestScore }}</span>
            </div>
          </div>

          <!-- 成績表格 -->
          <div class="table-wrapper">
            <table class="scores-table">
              <thead>
                <tr>
                  <!-- 按讚欄（僅在 webview + HT 名單時顯示） -->
                  <th v-if="showHTFeatures" class="awesome-header">{{ t('gameActManager.awesome') }}</th>
                  <th class="rank-header">{{ t('gameActManager.rank') }}</th>
                  <th>{{ t('gameActManager.seatNumber') }}</th>
                  <th>{{ t('gameActManager.studentName') }}</th>
                  <th @click="sortByScore" class="sortable">
                    {{ t('gameActManager.score') }}
                    <svg-icon icon-class="arrow-down" class="sort-icon" :class="{ 'rotate-up': sortOrder === 'ASC' }" />
                  </th>
                  <th>{{ t('gameActManager.correctCount') }}</th>
                  <th>{{ t('gameActManager.wrongCount') }}</th>
                  <th>{{ t('gameActManager.submitTime') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(score, index) in scores" :key="score.id" :class="{ 'top-rank': getRank(index) <= 3 }">
                  <!-- 按讚按鈕（僅在 webview + HT 名單時顯示） -->
                  <td v-if="showHTFeatures" class="awesome-cell">
                    <button
                      class="awesome-btn"
                      @click="handleAwesome(score.seatNumber)"
                      :disabled="!score.seatNumber"
                      :title="score.seatNumber ? t('gameActManager.clickToAwesome') : t('gameActManager.noSeatNumber')"
                    >
                      <svg-icon icon-class="thumbs-up" />
                    </button>
                  </td>
                  <td class="rank-cell">
                    <svg-icon v-if="getRank(index) === 1" icon-class="trophy-gold" class="trophy-icon" />
                    <svg-icon v-else-if="getRank(index) === 2" icon-class="trophy-silver" class="trophy-icon" />
                    <svg-icon v-else-if="getRank(index) === 3" icon-class="trophy-bronze" class="trophy-icon" />
                    <span v-else class="rank-number">{{ getRank(index) }}</span>
                  </td>
                  <td class="seat-number-cell">{{ score.seatNumber || '-' }}</td>
                  <td class="student-name">{{ score.studentName }}</td>
                  <td class="score-cell">{{ score.score }}</td>
                  <td class="correct-cell">{{ score.correctCount }}</td>
                  <td class="wrong-cell">{{ score.wrongCount }}</td>
                  <td class="time-cell">{{ formatTime(score.submitTime) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 分頁控制 -->
          <div v-if="totalPages > 1" class="pagination">
            <button
              class="page-btn"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              {{ t('gameActManager.prevPage') }}
            </button>
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <button
              class="page-btn"
              :disabled="currentPage >= totalPages"
              @click="changePage(currentPage + 1)"
            >
              {{ t('gameActManager.nextPage') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import SvgIcon from '../../components/SvgIcon.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import { useGameMessageBridge } from '@/composable/useGameMessageBridge';

const { t } = useI18n();
const $Message = inject('$Message');
const { messageBridge } = useGameMessageBridge('ActivityScoresPanel');

// Props
const props = defineProps({
  activityId: {
    type: String,
    required: true
  },
  activityName: {
    type: String,
    required: true
  },
  rosterSource: {
    type: String,
    default: 'general'
  }
});

// Emits
const emit = defineEmits(['back']);

// 狀態變數
const loading = ref(true);
const scores = ref([]);
const totalCount = ref(0);
const totalPages = ref(0);
const currentPage = ref(1);
const pageSize = ref(50); // 每頁顯示數量
const sortOrder = ref('DESC'); // 'DESC' or 'ASC'

// WebView 環境檢測
const isWebview = computed(() => {
  return window?.chrome?.webview !== undefined;
});

// 判斷是否顯示 HT 專屬功能（只要在 webview 內即可）
const showHTFeatures = computed(() => {
  return isWebview.value;
});

// 計算統計資料
const averageScore = computed(() => {
  if (scores.value.length === 0) return 0;
  const sum = scores.value.reduce((acc, score) => acc + score.score, 0);
  return sum / scores.value.length;
});

const highestScore = computed(() => {
  if (scores.value.length === 0) return 0;
  return Math.max(...scores.value.map(s => s.score));
});

// 計算實際排名（考慮分頁）
const getRank = (index) => {
  return (currentPage.value - 1) * pageSize.value + index + 1;
};

// 載入成績資料
const loadScores = async () => {
  loading.value = true;
  try {
    const response = await gameActivityAPI.getActivityScores({
      activityId: props.activityId,
      sortField: 'score',
      sortOrder: sortOrder.value,
      pageSize: pageSize.value,
      pageNumber: currentPage.value
    });

    if (response.status === 200) {
      scores.value = response.data;
      totalCount.value = response.totalCount;
      totalPages.value = response.totalPages;
    }
  } catch (error) {
    console.error('載入成績失敗:', error);
    $Message.error(t('gameActManager.loadScoresFailed'));
  } finally {
    loading.value = false;
  }
};

// 切換排序
const sortByScore = () => {
  sortOrder.value = sortOrder.value === 'DESC' ? 'ASC' : 'DESC';
  loadScores();
};

// 換頁
const changePage = (page) => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  loadScores();
};

// 格式化時間
const formatTime = (timestamp) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp * 1000);
  return date.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// 返回
const handleBack = () => {
  emit('back');
};

// 點擊遮罩層關閉面板
const handleOverlayClick = () => {
  emit('back');
};

// 按讚功能
const handleAwesome = (seatNumber) => {
  if (!seatNumber) {
    $Message.warning(t('gameActManager.noSeatNumber'));
    return;
  }

  try {
    // 從 sessionStorage 讀取 class_id
    const classId = sessionStorage.getItem('ht_class_id') || '';

    // 判斷是否使用 HT 名單
    const useHtMemberList = props.rosterSource === 'HT';

    messageBridge.postMessage({
      action: "Inst.Awesome",
      class_id: classId,
      useHtMemberList: useHtMemberList,
      clientType: "GameManager",
      awesomeList: [seatNumber]
    });
    console.log({
      action: "Inst.Awesome",
      class_id: classId,
      useHtMemberList: useHtMemberList,
      clientType: "GameManager",
      awesomeList: [seatNumber]
    });
  } catch (error) {
    console.error('發送按讚失敗:', error);
    $Message.error(t('gameActManager.awesomeFailed'));
  }
};

// 發送成績功能（座號去重、取高分）
const handleSendScores = () => {
  if (scores.value.length === 0) {
    $Message.warning(t('gameActManager.noScoresToSend'));
    return;
  }

  try {
    // 座號去重，取高分
    const scoreMap = new Map();

    scores.value.forEach(score => {
      const seatNumber = score.seatNumber;
      if (!seatNumber) return; // 跳過沒有座號的資料

      // 如果該座號尚未記錄，或新分數更高，則更新
      if (!scoreMap.has(seatNumber) || scoreMap.get(seatNumber).score < score.score) {
        scoreMap.set(seatNumber, {
          seatNumber: score.seatNumber,
          studentName: score.studentName,
          score: score.score,
          correctCount: score.correctCount,
          wrongCount: score.wrongCount,
          submitTime: score.submitTime
        });
      }
    });

    // 轉換為陣列
    const uniqueScores = Array.from(scoreMap.values());

    if (uniqueScores.length === 0) {
      $Message.warning(t('gameActManager.noValidScores'));
      return;
    }

    // 從 sessionStorage 讀取 class_id
    const classId = sessionStorage.getItem('ht_class_id') || '';

    // 判斷是否使用 HT 名單
    const useHtMemberList = props.rosterSource === 'HT';

    // 發送 postMessage
    messageBridge.postMessage({
      action: "Send.GameScoreList",
      class_id: classId,
      useHtMemberList: useHtMemberList,
      data: uniqueScores
    });

    console.log({
      action: "Send.GameScoreList",
      class_id: classId,
      useHtMemberList: useHtMemberList,
      data: uniqueScores
    });

    $Message.success(t('gameActManager.scoresSent'));
  } catch (error) {
    console.error('發送成績失敗:', error);
    $Message.error(t('gameActManager.sendScoresFailed'));
  }
};

// 組件掛載時載入成績
onMounted(() => {
  loadScores();
});
</script>

<style lang="less" scoped>
@import "../../assets/color.less";
@import "../../assets/gameActManager.less";

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.scores-panel-container {
  background: white;
  border-radius: 8px;
  width: 95%;
  max-width: 1000px;
  max-height: calc(90vh - 100px);  
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.panel-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  svg {
    width: 20px;
    height: 20px;
    color: #666;
  }

  &:hover {
    background-color: #f5f5f5;
  }
}

.panel-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  flex: 1;
}

.send-scores-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background-color: @game-primary-color;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background-color: darken(@game-primary-color, 10%);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
}

.panel-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

// 載入中樣式
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;

  p {
    margin-top: 16px;
    color: #999;
    font-size: 14px;
  }
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid @game-primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// 無資料樣式
.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;

  .empty-icon {
    font-size: 64px;
    color: #ccc;
    margin-bottom: 16px;
  }

  p {
    color: #999;
    font-size: 16px;
  }
}

// 統計資訊
.stats-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f9f9fb;
  border-radius: 8px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 13px;
  color: #666;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: @game-primary-color;
}

// 表格樣式
.table-wrapper {
  overflow-x: auto;
  margin-bottom: 20px;
}

.scores-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  thead {
    background-color: #f9f9fb;

    th {
      padding: 12px 16px;
      text-align: left;
      font-weight: 600;
      color: #333;
      border-bottom: 2px solid #e8e8e8;
      white-space: nowrap;

      &.sortable {
        cursor: pointer;
        user-select: none;
        transition: background-color 0.2s;
        display: table-cell;
        vertical-align: middle;

        &:hover {
          background-color: #f0f0f0;
        }

        .sort-icon {
          display: inline-block;
          margin-left: 4px;
          font-size: 12px;
          color: @game-primary-color;
          transition: transform 0.2s;
          vertical-align: middle;

          &.rotate-up {
            transform: rotate(180deg);
          }
        }
      }
    }
  }

  tbody {
    tr {
      transition: background-color 0.2s;

      &:hover {
        background-color: #f9f9fb;
      }

      &.top-rank {
        background-color: #fffbf0;
      }

      td {
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
      }
    }
  }
}

.rank-cell {
  text-align: center !important;
  font-weight: 600;
}

.trophy-icon {
  width: 36px;
  height: 36px;
  display: inline-block;
  vertical-align: middle;
}


.rank-number {
  color: #666;
}

.seat-number-cell {
  color: #333;
  font-weight: 600;
}

.student-name {
  font-weight: 600;
  color: #333;
}

.score-cell {
  font-weight: 700;
  color: @game-primary-color;
}

.correct-cell {
  color: #52c41a;
  font-weight: 600;
}

.wrong-cell {
  color: #ff4d4f;
  font-weight: 600;
}

.time-cell {
  color: #999;
  font-size: 13px;
}

// 按讚欄位樣式
.awesome-header {
  width: 60px;
  text-align: center;
}

.awesome-cell {
  text-align: center;
  padding: 8px !important;
}

// 排名標題置中
.rank-header {
  text-align: center !important;
}

.awesome-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: #fff;
  border: 2px solid @game-primary-color;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  color: @game-primary-color;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover:not(:disabled) {
    background-color: @game-primary-color;
    color: white;
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: scale(1.05);
  }

  &:disabled {
    border-color: #d9d9d9;
    color: #d9d9d9;
    cursor: not-allowed;
    opacity: 0.5;
  }
}

// 分頁樣式
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  background-color: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;

  &:hover:not(:disabled) {
    border-color: @game-primary-color;
    color: @game-primary-color;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.page-info {
  font-size: 14px;
  color: #666;
}

// 響應式設計
@media (max-width: 768px) {
  .scores-panel-container {
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .stats-summary {
    flex-direction: column;
    gap: 12px;
  }

  .scores-table {
    font-size: 12px;

    thead th,
    tbody td {
      padding: 8px 12px;
    }
  }
}
</style>
